/**
 * Sesión y tokens — fuente única de verdad.
 *
 * Existe por un bug que expulsaba al operador cada hora con la pantalla
 * "Required parameters missing" de Cognito. Había dos formas de mandar al login
 * y una estaba rota:
 *
 *   - AuthContext PARSEABA `VITE_COGNITO_LOGIN_URL` para sacarle el host y el
 *     client_id, y armaba una URL de authorize completa (PKCE). Funcionaba.
 *   - api/client.ts NAVEGABA directo a esa misma variable ante un 401. Pero la
 *     variable solo trae `client_id` — sin response_type, redirect_uri ni scope,
 *     que es exactamente lo que Cognito reclama.
 *
 * Ahora hay una sola función que arma el redirect, y ambos la importan.
 *
 * Ciclo de vida de la sesión:
 *   - access token: 60 min (lo emite Cognito)
 *   - se renueva en silencio con el refresh token cuando el API responde 401
 *   - tope duro de 12 h desde el login; pasado eso se vuelve a autenticar
 *   - el corte por inactividad (2 h) lo maneja useIdleTimeout, aparte de esto
 *
 * El tope de 12 h se aplica en dos lugares a propósito: acá para que el corte
 * sea inmediato en el navegador, y en el propio refresh token de Cognito
 * (`refresh_token_validity`, stacks/main_stack.py) que es la fuente autoritativa.
 * Si alguien manipula el sessionStorage, el servidor igual rechaza el refresh.
 */

export const JWT_KEY = 'pgh_jwt'                    // access_token — va al API Gateway
export const ID_TOKEN_KEY = 'pgh_id_token'          // id_token — de acá salen email y grupos
export const REFRESH_KEY = 'pgh_refresh_token'
export const PKCE_VERIFIER_KEY = 'pgh_pkce_verifier'
export const SESSION_START_KEY = 'pgh_session_start'

/** Duración máxima de una sesión antes de pedir login de nuevo. */
export const SESSION_MAX_MS = 12 * 60 * 60 * 1000

/** Se emite cuando la renovación silenciosa deja tokens nuevos. */
export const TOKENS_REFRESHED = 'pgh:tokens-refreshed'

// ── Configuración de Cognito ────────────────────────────────────────────────
// `VITE_COGNITO_LOGIN_URL` es una semilla, no un destino: de ella salen el host
// y el client_id. Nunca navegar a ella directamente.

function loginUrlSeed(): string {
  return (import.meta.env.VITE_COGNITO_LOGIN_URL as string) || ''
}

export function cognitoBaseUrl(): string {
  try {
    const u = new URL(loginUrlSeed())
    return `${u.protocol}//${u.host}`
  } catch {
    return loginUrlSeed()
  }
}

export function cognitoClientId(): string {
  try {
    return new URL(loginUrlSeed()).searchParams.get('client_id') ?? ''
  } catch {
    return ''
  }
}

/** Debe coincidir con una de las callback URLs del app client de Cognito. */
export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`
}

// ── PKCE ────────────────────────────────────────────────────────────────────

function randomBase64Url(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength))
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function sha256Base64Url(plain: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain))
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Manda al login de Cognito con todos los parámetros que exige el flujo PKCE.
 *
 * Es el ÚNICO camino válido hacia el login. Navegar a `VITE_COGNITO_LOGIN_URL`
 * a mano produce "Required parameters missing".
 */
export async function redirectToLogin(): Promise<void> {
  const verifier = randomBase64Url(32)
  const challenge = await sha256Base64Url(verifier)
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoClientId(),
    redirect_uri: redirectUri(),
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge,
  })
  window.location.href = `${cognitoBaseUrl()}/oauth2/authorize?${params}`
}

// ── Estado de la sesión ─────────────────────────────────────────────────────

export function markSessionStart(): void {
  sessionStorage.setItem(SESSION_START_KEY, String(Date.now()))
}

/**
 * ¿Se pasó de las 12 h desde el login?
 *
 * Sin marca de inicio devuelve `true`: preferimos pedir login de más que
 * mantener viva una sesión de origen desconocido.
 */
export function sessionAgeExceeded(): boolean {
  const raw = sessionStorage.getItem(SESSION_START_KEY)
  if (!raw) return true
  const started = Number(raw)
  if (!Number.isFinite(started) || started <= 0) return true
  return Date.now() - started > SESSION_MAX_MS
}

/** Milisegundos que le quedan a la sesión (0 si ya venció). */
export function sessionTimeLeftMs(): number {
  const raw = sessionStorage.getItem(SESSION_START_KEY)
  if (!raw) return 0
  return Math.max(0, SESSION_MAX_MS - (Date.now() - Number(raw)))
}

export function clearSession(): void {
  sessionStorage.removeItem(JWT_KEY)
  sessionStorage.removeItem(ID_TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(SESSION_START_KEY)
}

function storeTokens(data: Record<string, unknown>): string | null {
  const access = typeof data['access_token'] === 'string' ? data['access_token'] : null
  const id = typeof data['id_token'] === 'string' ? data['id_token'] : null
  // El refresh sólo viene en el intercambio inicial; al renovar no se reemite,
  // así que no se pisa el que ya está guardado.
  const refresh = typeof data['refresh_token'] === 'string' ? data['refresh_token'] : null

  if (access) sessionStorage.setItem(JWT_KEY, access)
  if (id) sessionStorage.setItem(ID_TOKEN_KEY, id)
  if (refresh) sessionStorage.setItem(REFRESH_KEY, refresh)
  return id ?? access
}

// ── Intercambio y renovación de tokens ──────────────────────────────────────

/** Canjea el `?code=` del callback por tokens. Arranca el reloj de la sesión. */
export async function exchangeCodeForTokens(code: string): Promise<string | null> {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
  if (!verifier) return null
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cognitoClientId(),
    redirect_uri: redirectUri(),
    code,
    code_verifier: verifier,
  })

  try {
    const resp = await fetch(`${cognitoBaseUrl()}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
    if (!resp.ok) return null
    const token = storeTokens(await resp.json() as Record<string, unknown>)
    if (token) markSessionStart()
    return token
  } catch {
    return null
  }
}

// Una sola renovación en vuelo: si tres requests reciben 401 a la vez, las tres
// esperan el mismo refresh en lugar de disparar tres y competir entre sí.
let refreshInFlight: Promise<boolean> | null = null

/**
 * Renueva el access token con el refresh token.
 *
 * Devuelve `false` si no hay refresh token, si la sesión pasó las 12 h, o si
 * Cognito rechaza — en todos esos casos el llamador debe mandar al login.
 */
export function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    if (sessionAgeExceeded()) return false

    const refresh = sessionStorage.getItem(REFRESH_KEY)
    if (!refresh) return false

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cognitoClientId(),
      refresh_token: refresh,
    })

    try {
      const resp = await fetch(`${cognitoBaseUrl()}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })
      if (!resp.ok) return false
      const token = storeTokens(await resp.json() as Record<string, unknown>)
      if (!token) return false
      window.dispatchEvent(new CustomEvent(TOKENS_REFRESHED))
      return true
    } catch {
      return false
    } finally {
      // Se libera en el próximo tick para que las requests que llegaron durante
      // la renovación reusen este resultado y no arranquen otra.
      setTimeout(() => { refreshInFlight = null }, 0)
    }
  })()

  return refreshInFlight
}
